import type {
  DocumentData,
  DocumentReference,
  FieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  CollectionReference,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore'
import { StaticModelCollection } from '../../firestore-mobx/model'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SlideQuestion } from '../../models/SlideQuestion'
import type { FirestoreSlideQuestion } from './schema'
import { empty, schema } from './schema'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'

const converter: FirestoreDataConverter<FirestoreSlideQuestion> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })

    return schema.parse(data)
  },
}

export const getColRef = (
  firestore: Firestore,
  { slideDeckId }: { slideDeckId: string }
): CollectionReference<FirestoreSlideQuestion, DocumentData> => {
  return collection(
    firestore,
    'slide_deck',
    slideDeckId,
    'question'
  ).withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  {
    slideDeckId,
    slideQuestionId,
  }: { slideDeckId: string; slideQuestionId: string }
): DocumentReference<FirestoreSlideQuestion, DocumentData> => {
  return doc(
    getColRef(firestore, {
      slideDeckId: slideDeckId,
    }),
    slideQuestionId
  )
}

export const buildEmptyQuestionCollection = (
  repository: FirebaseRepository
) => {
  return new StaticModelCollection({
    repository,
    model: SlideQuestion,
    empty: empty,
  })
}

/**
 * Get List of SlideQuestion models from Firestore
 *
 * @param repository - FirebaseRepository instance
 * @param slideDeckId - ID of the SlideDeck to get questions for
 *
 * @returns Stream for SlideQuestion snapshots
 */
export const getSlideQuestions = (
  repository: FirebaseRepository,
  { slideDeckId }: { slideDeckId: string }
) => {
  const ref = getColRef(repository.firestore, { slideDeckId })

  return modelListStream(repository, ref, SlideQuestion)
}

export const fetchSlideQuestions = async (
  repository: FirebaseRepository,
  params: { slideDeckId: string }
) => {
  const docRef = getColRef(repository.firestore, params)

  const docs = await getDocsWithError(docRef, 'FetchSlideQuestionsError')

  return docs.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, SlideQuestion)
  })
}

export type QuestionFieldsForUpload = Omit<FirestoreSlideQuestion, 'updatedAt'>

export const saveSlideDeckQuestion = async (
  repository: FirebaseRepository,
  {
    questionFields,
    slideDeckId,
    questionId,
  }: {
    slideDeckId: string
    questionFields: QuestionFieldsForUpload
    questionId?: string
  }
) => {
  interface WriteInterface extends Omit<FirestoreSlideQuestion, 'updatedAt'> {
    updatedAt: FieldValue
  }

  const dataToWrite: WriteInterface = {
    ...questionFields,
    updatedAt: serverTimestamp(),
  }

  const ref = questionId
    ? doc(getColRef(repository.firestore, { slideDeckId }), questionId)
    : getColRef(repository.firestore, { slideDeckId })

  if (ref instanceof CollectionReference) {
    return await addDocWithError(
      ref,
      dataToWrite,
      'CreateSlideDeckQuestionError'
    )
  }

  return await updateDocWithError(
    ref,
    //@ts-expect-error - todo: not sure why the type system hates this
    dataToWrite,
    'UpdateSlideDeckQuestionError'
  )
}

export const deleteSlideDeckQuestion = async (
  repository: FirebaseRepository,
  {
    questionId,
    slideDeckId,
  }: {
    questionId: string
    slideDeckId: string
  }
) => {
  await deleteDocWithError(
    doc(getColRef(repository.firestore, { slideDeckId }), questionId),
    'DeleteSlideDeckQuestionError'
  )
}

type StreamingAudioOptions = {
  questionId: string
  slideDeckId: string
  abortSignal?: AbortSignal
  noPlaceholderAudio?: boolean
}

/**
 * Get streaming audio from the server for a talk track
 *
 * @param {boolean} opts.noPlaceholderAudio - if true, will return an empty audio object if the audio is a placeholder
 * @returns {Promise<{ audio: Audio; isPlaceholder: boolean }>} - returns an object with the audio and a boolean indicating if it is a placeholder audio
 */
export const getTalkTrackAudio = async (
  repository: FirebaseRepository,
  opts: StreamingAudioOptions
) => {
  return getStreamingAudio(repository, 'streamTalkTrackPreview', opts)
}

/**
 * Get streaming audio from the server for the solo bot
 *
 * @param {boolean} opts.noPlaceholderAudio - if true, will return an empty audio object if the audio is a placeholder
 * @returns {Promise<{ audio: Audio; isPlaceholder: boolean }>} - returns an object with the audio and a boolean indicating if it is a placeholder audio
 */
export const getSoloBotAudio = async (
  repository: FirebaseRepository,
  opts: StreamingAudioOptions
) => {
  return getStreamingAudio(repository, 'streamTalkTrackPreview', opts)
}

/**
 * Get streaming audio from the server
 *
 * @param {FirebaseRepository} repository - Firebase repository instance
 * @param {string} functionName - name of the function to call - either 'streamTalkTrackPreview'
 *
 * @param {boolean} opts.noPlaceholderAudio - if true, will return an empty audio object if the audio is a placeholder
 *
 * @returns {Promise<{ audio: Audio; isPlaceholder: boolean }>} - returns an object with the audio and a boolean indicating if it is a placeholder audio
 */
const getStreamingAudio = async (
  repository: FirebaseRepository,
  functionName: 'streamTalkTrackPreview',
  {
    questionId,
    slideDeckId,
    abortSignal,
    noPlaceholderAudio = false,
  }: StreamingAudioOptions
) => {
  const region = repository.functions.region

  const isEmu = !!repository.auth.emulatorConfig
  const project = repository.firestore.app.options.projectId
  const isProd = project === 'breakout-learning-cases'

  const requestURL = isEmu
    ? `http://localhost:5001/${project}/${region}/${functionName}`
    : `https://${isProd ? 'app' : 'dev'}.breakoutlearning.com/api/${functionName}`

  const url = new URL(requestURL)
  url.searchParams.append('slideDeckId', slideDeckId)
  url.searchParams.append('questionId', questionId)

  // fetch from the URL for wav data
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${repository.authToken}`,
    },
    signal: abortSignal,
  })

  if (!res.ok) throw new Error(`${functionName} failed: ${res.statusText}`)

  const isPlaceholder = res.status === 202

  if (noPlaceholderAudio && isPlaceholder)
    return { isPlaceholder: true, audio: new Audio() }

  // wait for the buffer data
  const wavBuffer = await res.arrayBuffer()

  // make wav blob
  const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' })
  return {
    audio: new Audio(URL.createObjectURL(wavBlob)),
    isPlaceholder, //202 status if "please try again" audio
  }
}

export const sortSlideQuestions = async (
  repository: FirebaseRepository,
  {
    currentOrder,
    oldIndex,
    newIndex,
    slideDeckId,
  }: {
    slideDeckId: string
    currentOrder: string[]
    oldIndex?: number
    newIndex?: number
  }
) => {
  const questionIds = [...currentOrder]

  // if old index and new index supplied then reorder
  if (oldIndex !== undefined && newIndex !== undefined) {
    //remove at old index
    const targetId = questionIds.splice(oldIndex, 1)[0]

    //insert at new index
    questionIds.splice(newIndex, 0, targetId)
  }

  const batch = writeBatch(repository.firestore)
  questionIds.forEach((slideQuestionId, index) => {
    // get the reference doc ref
    const questionRef = getDocRef(repository.firestore, {
      slideDeckId,
      slideQuestionId,
    })
    batch.update(questionRef, { order: index })
  })

  // perform the batch write
  await batch.commit()
}
